import { useMemo, useState } from 'react';
import { Flex } from '@mantine/core';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	CustomDetailDataTypeEnum,
	GetInvestorDetailsWithPersonRes,
} from '@dealstack/shared/lib/generated';
import { getBooleanLabel } from '@dealstack/frontend/shared/other/react/booleanLabel';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestorDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsCard/InvestorDetailsCard.tsx';
import { CustomFieldRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/CustomFieldsCard/CustomFieldRow.tsx';
import {
	getEditableInvestorCustomFields,
	getInvestorCustomFields,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/CustomFieldsCard/customFields.utils.ts';
import { CustomFieldsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/CustomFieldsCard/CustomFieldsDrawer.tsx';

interface Props {
	investorId: string;
	investor: GetInvestorDetailsWithPersonRes | undefined;
	isLoading?: boolean;
	onSaved: () => void;
}

export const CustomFieldsCard = ({ investorId, investor, isLoading = false, onSaved }: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();
	const [isDrawerOpen, setDrawerOpen] = useState(false);

	const customFields = useMemo(
		() => getInvestorCustomFields(investor?.mipCustomDetails, investor?.investorCustomDetails),
		[investor]
	);

	const editableFields = useMemo(
		() => getEditableInvestorCustomFields(customFields),
		[customFields]
	);

	if (isLoading || !customFields.length) {
		return null;
	}

	return (
		<>
			<InvestorDetailsCard
				title={tMipManage('investors.details.custom_fields.title')}
				action={
					editableFields.length
						? { kind: 'edit', onEdit: () => setDrawerOpen(true) }
						: undefined
				}
				dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_CUSTOM_FIELDS_CARD}
			>
				<Flex direction="column" gap="0.25rem">
					{customFields.map((customField) => (
						<CustomFieldRow
							key={customField.id}
							label={customField.name}
							value={
								customField.dataType === CustomDetailDataTypeEnum.BOOLEAN
									? getBooleanLabel(customField.value, tCommonUi)
									: customField.value
							}
						/>
					))}
				</Flex>
			</InvestorDetailsCard>

			<CustomFieldsDrawer
				opened={isDrawerOpen}
				onClose={() => setDrawerOpen(false)}
				investorId={investorId}
				customFields={editableFields}
				onSaved={onSaved}
			/>
		</>
	);
};
